import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { useCustomerId } from "../../hooks/useCustomerId";
import { listLoads } from "../../api/loads";
import type { LoadSummary } from "../../features/load/domain/types";
import {
  PageLayout,
  PageHeader,
  Card,
  Button,
  Badge,
  PageSpinner,
  EmptyState,
} from "../../components";
import { LoadCreateModal } from "../../features/load/components/LoadCreateModal";

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge>No runs</Badge>;
  const colors: Record<string, string> = {
    completed: "text-green-400 bg-green-400/10",
    running: "text-quantum-400 bg-quantum-400/10",
    queued: "text-yellow-400 bg-yellow-400/10",
    failed: "text-red-400 bg-red-400/10",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-body-sm ${colors[status] || ""}`}>
      {status}
    </span>
  );
}

export default function Loads() {
  const { token, isAdmin } = useAuth();
  const customerId = useCustomerId(token);
  const [showCreate, setShowCreate] = useState(false);

  const { data: loads, isLoading } = useQuery<LoadSummary[]>({
    queryKey: ["loads", customerId],
    queryFn: () => listLoads(token!),
    enabled: !!token,
  });

  if (isLoading) return <PageSpinner />;

  return (
    <PageLayout>
      <PageHeader
        title="Loads"
        subtitle="Datacenter / industrial / commercial demand — modeled against the grid that serves it (full ReCiPe lifecycle — air, soil & water)"
        actions={
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            New Load
          </Button>
        }
      />

      {loads && loads.length > 0 ? (
        <div className="space-y-3">
          {loads.map((l) => (
            <Link key={l.load_id} to={l.load_id} className="block">
              <Card>
                <div className="p-4 flex items-center justify-between hover:bg-surface-600 transition-colors rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-body font-medium text-text-primary">
                        {l.load_name}
                      </span>
                      <StatusBadge status={l.latest_run_status} />
                      {l.existing_load_flag ? (
                        <Badge>Existing</Badge>
                      ) : (
                        <Badge>Future</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-body-sm text-text-secondary">
                      {l.state && <span>{l.state}</span>}
                      {l.eia_ba_code && <span>{l.eia_ba_code}</span>}
                      <span>{l.peak_load_mw} MW peak</span>
                      <span>PUE {l.pue}</span>
                      {l.offset_count > 0 && (
                        <span>
                          {l.offset_count} offset{l.offset_count !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-text-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No loads yet"
          action={{ label: "Create Load", onClick: () => setShowCreate(true) }}
        />
      )}

      <LoadCreateModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        customerId={customerId || ""}
        isAdmin={!!isAdmin}
      />
    </PageLayout>
  );
}
